import { useState } from "react";
import { submitEnquiry } from "@/lib/api";
import { openWhatsApp } from "@/data/site";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { ArrowUpRight, MessageCircle, Loader2 } from "lucide-react";
import { PRODUCTS } from "@/data/products";

export const EnquiryForm = ({
  variant = "full",
  defaultInterest = "",
  source = "contact-form",
}) => {
  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    company: "",
    location: "",
    interest: defaultInterest,
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim()) {
      toast.error("Please share your name and phone number.");
      return;
    }
    setLoading(true);
    try {
      await submitEnquiry({ ...form, source });
      setDone(true);
      toast.success(
        "Enquiry received. Our team will call you within business hours.",
      );
      setForm({
        name: "",
        phone: "",
        email: "",
        company: "",
        location: "",
        interest: defaultInterest,
        message: "",
      });
    } catch (err) {
      toast.error("Couldn't submit right now. Please try WhatsApp or call us.");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const waMessage =
    `Hi Thermo Edge, I'm ${form.name || "[name]"} from ${form.location || "[location]"}. I'm interested in ${form.interest || "insulation"}. ${form.message || ""}`.trim();

  const inputCls =
    "w-full bg-transparent border-b border-white/20 focus:border-edge outline-none py-3 text-white placeholder:text-slateink/50 focus:ring-0 transition-colors";

  return (
    <form onSubmit={submit} className="grid gap-6" data-testid="enquiry-form">
      <div className="grid md:grid-cols-2 gap-6">
        <div>
          <label className="text-[10px] tracking-[0.25em] text-slateink uppercase">
            Your Name *
          </label>
          <input
            type="text"
            value={form.name}
            onChange={update("name")}
            className={inputCls}
            data-testid="enquiry-name"
            required
            placeholder="e.g. Rakesh Sharma"
          />
        </div>
        <div>
          <label className="text-[10px] tracking-[0.25em] text-slateink uppercase">
            Phone / WhatsApp *
          </label>
          <input
            type="tel"
            value={form.phone}
            onChange={update("phone")}
            className={inputCls}
            data-testid="enquiry-phone"
            required
            placeholder="10-digit number"
          />
        </div>
        {variant === "full" && (
          <>
            <div>
              <label className="text-[10px] tracking-[0.25em] text-slateink uppercase">
                Email
              </label>
              <input
                type="email"
                value={form.email}
                onChange={update("email")}
                className={inputCls}
                data-testid="enquiry-email"
                placeholder="you@company.com"
              />
            </div>
            <div>
              <label className="text-[10px] tracking-[0.25em] text-slateink uppercase">
                Company
              </label>
              <input
                type="text"
                value={form.company}
                onChange={update("company")}
                className={inputCls}
                data-testid="enquiry-company"
                placeholder="Optional"
              />
            </div>
            <div>
              <label className="text-[10px] tracking-[0.25em] text-slateink uppercase">
                Site Location
              </label>
              <input
                type="text"
                value={form.location}
                onChange={update("location")}
                className={inputCls}
                data-testid="enquiry-location"
                placeholder="City, district"
              />
            </div>
            <div>
              <label className="text-[10px] tracking-[0.25em] text-slateink uppercase">
                I'm interested in
              </label>
              <select
                value={form.interest}
                onChange={update("interest")}
                className={`${inputCls} appearance-none`}
                data-testid="enquiry-interest"
              >
                <option value="" className="bg-ink-900">
                  Select product / service
                </option>
                {PRODUCTS.map((p) => (
                  <option key={p.slug} value={p.name} className="bg-ink-900">
                    {p.name}
                  </option>
                ))}
                <option value="Site Inspection" className="bg-ink-900">
                  Free Site Inspection
                </option>
                <option value="Installation Service" className="bg-ink-900">
                  Installation Service
                </option>
                <option value="Consultation" className="bg-ink-900">
                  Technical Consultation
                </option>
              </select>
            </div>
          </>
        )}
      </div>

      <div>
        <label className="text-[10px] tracking-[0.25em] text-slateink uppercase">
          Project Details
        </label>
        <textarea
          rows={variant === "full" ? 4 : 3}
          value={form.message}
          onChange={update("message")}
          className={`${inputCls} resize-none`}
          data-testid="enquiry-message"
          placeholder="Roof area, material, timeline — anything useful."
        />
      </div>

      <div className="flex flex-col sm:flex-row gap-4 pt-4">
        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          type="submit"
          disabled={loading}
          className="group inline-flex items-center justify-center gap-2 bg-edge text-ink-950 font-bold text-sm px-8 py-5 uppercase tracking-wider hover:bg-edge-hover transition-colors disabled:opacity-50"
          data-testid="enquiry-submit"
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
          {done ? "Sent — we'll be in touch" : "Send Enquiry"}
          <ArrowUpRight className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
        </motion.button>
        <button
          type="button"
          onClick={() => openWhatsApp(waMessage)}
          className="inline-flex items-center justify-center gap-2 border border-white/30 hover:border-edge text-white font-bold text-sm px-8 py-5 uppercase tracking-wider transition-colors"
          data-testid="enquiry-whatsapp"
        >
          <MessageCircle className="w-4 h-4" /> WhatsApp Instead
        </button>
      </div>
    </form>
  );
};

export default EnquiryForm;
